import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import Styles from "../styles/Posts.module.css";
import { postsAPI } from "../services/api";
import { UserContext } from "./context/user/UserContext";
import { useDispatch, useSelector } from "react-redux";
import { setPosts, replacePosts, removePost, updatePost } from "../redux/slices/PostsSlice";
import Post from "./Post";
import Loader from "./Loader";
import Toast from "./Toast";
import ConfirmModal from "./ConfirmModal";

const Posts = () => {
  const { userData } = useContext(UserContext);
  const token = userData.token;
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const posts = useSelector((state) => state.posts.posts);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const observer = useRef();
  const [postsEnd, setPostsEnd] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, postId: null });

  const lastPostElementRef = useCallback(
    (node) => {
      console.log("Inside lastPostElementRef, page:", page, "totalPages:", totalPages);
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        console.log("Intersection observed:", entries[0].isIntersecting);
        if (entries[0].isIntersecting && page < totalPages) {
          console.log("Loading next page...");
          setPage((prevPageNumber) => prevPageNumber + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, totalPages, page]
  );
  const prevSearchRef = useRef(search);
  const getPosts = useCallback(async (searchTerm) => {
    setLoading(true);
    setPostsEnd(false);
    try {
      const response = await postsAPI.getAllPosts(token, page, searchTerm);
      const data = response.data;
      setTotalPages(response.totalPages);
      if (prevSearchRef.current !== searchTerm || page === 1) {
        dispatch(replacePosts(data));
      } else {
        dispatch(setPosts(data));
      }
      prevSearchRef.current = searchTerm;
      if (page === response.totalPages) {
        setPostsEnd(true);
      }
    } catch (error) {
      console.log("Some error occurred:", error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, page, token]);

  const handleDeleteClick = (postId) => {
    setConfirmModal({ isOpen: true, postId });
  };

  const handleDeleteConfirm = async () => {
    const postId = confirmModal.postId;
    setToast({ message: 'Deleting post...', type: 'info' });
    
    try {
      await postsAPI.deletePost(token, postId);
      
      // Only remove from list AFTER successful deletion
      dispatch(removePost(postId));
      
      setToast({ message: 'Post deleted successfully! ✅', type: 'success' });
    } catch (error) {
      console.log("Error in deleting:", error);
      setToast({ message: `Failed to delete post: ${error.message}`, type: 'error' });
    }
  };

  const handleUpdate = async (postId, updates) => {
    setToast({ message: 'Updating post...', type: 'info' });
    
    try {
      await postsAPI.updatePost(token, postId, updates);
      
      // Immediately update in list
      dispatch(updatePost({ id: postId, updates }));
      
      setToast({ message: 'Post updated successfully! ✅', type: 'success' });
    } catch (error) {
      console.log("Error updating post:", error);
      setToast({ message: `Failed to update post: ${error.message}`, type: 'error' });
    }
  };
  const clear = () => {
    setSearch("");
    setPostsEnd(false);
    if (page === 1) {
      getPosts("");
    } else {
      setPage(1);
    }
  };
  useEffect(() => {
    dispatch(replacePosts([]));
    setPostsEnd(false);
    setPage(1);
  }, [dispatch, search]);
  useEffect(() => {
    getPosts(search);
  }, [getPosts, search]);
  return (
    <div className={Styles.container}>
      <div className={Styles.heading}>
        <h2>POSTS</h2>
      </div>
      <div className={Styles.inputContainer}>
        <input
          type="text"
          value={search}
          className={Styles.input}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <button className={Styles.clear_button} onClick={clear}>
            X
          </button>
        )}
        <button
          className={Styles.search_button}
          onClick={() => getPosts(search)}
        >
          Search
        </button>
      </div>

      <div className={Styles.posts_list}>
        <table>
          <thead>
            <tr>
              <th>No</th>
              <th>Post ID</th>
              <th>Title</th>
              <th>Description</th>
              <th>
                Reasons <br /> (Reporting)
              </th>
              <th>Username</th>
              <th>User ID</th>
              <th>Action</th>
            </tr>
          </thead>

          {posts.map((post, index) => {
            if (posts.length === index + 1) {
              return (
                <tbody ref={lastPostElementRef} key={index}>
                  <Post
                    post={post}
                    index={index}
                    page={page}
                    handleDelete={handleDeleteClick}
                    handleUpdate={handleUpdate}
                  />
                </tbody>
              );
            } else {
              return (
                <tbody key={index}>
                  <Post
                    post={post}
                    index={index}
                    page={page}
                    handleDelete={handleDeleteClick}
                    handleUpdate={handleUpdate}
                  />
                </tbody>
              );
            }
          })}
        </table>
      </div>
      <div>{postsEnd && !loading && <h3>No More Posts</h3>}</div>
      {loading && <Loader />}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, postId: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Location?"
        message="Are you sure you want to delete this scary location? This action cannot be undone and will permanently remove this location from the database."
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
};

export default Posts;
